// src/components/message-board/MessageBoardPreview.js
import React, { useState, useEffect } from 'react';
import { MessageSquare, ChevronRight, Send, X } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import MessageComposer from './MessageComposer';

// Lightweight content renderer — clickable links + small YouTube thumbnails
const renderMessageContent = (text, className = '') => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
    }
    const url = match[0];
    // Check for YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) {
      parts.push({ type: 'youtube', videoId: ytMatch[1], url });
    } else {
      parts.push({ type: 'link', url });
    }
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }
  if (parts.length === 0) parts.push({ type: 'text', content: text });

  return (
    <div className={className}>
      {parts.map((part, i) => {
        if (part.type === 'youtube') {
          return (
            <a key={i} href={part.url} target="_blank" rel="noopener noreferrer"
              className="block my-1 rounded overflow-hidden border border-neutral-700 hover:border-yellow-500 transition-colors"
              style={{ maxWidth: 280 }}>
              <img src={`https://img.youtube.com/vi/${part.videoId}/mqdefault.jpg`} alt="YouTube"
                className="w-full" />
              <div className="flex items-center gap-1.5 px-2 py-1 bg-neutral-800 text-xs text-red-400">
                <span>▶</span> YouTube
              </div>
            </a>
          );
        }
        if (part.type === 'link') {
          return (
            <a key={i} href={part.url} target="_blank" rel="noopener noreferrer"
              className="text-yellow-400 hover:underline break-all">
              {part.url.length > 50 ? part.url.substring(0, 50) + '...' : part.url}
            </a>
          );
        }
        return <span key={i}>{part.content}</span>;
      })}
    </div>
  );
};

const MessageBoardPreview = ({ leagueId, onViewAll }) => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total_messages: 0, unread_messages: 0 });
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => {
    if (leagueId) {
      loadPreview();
    }
  }, [leagueId]);

  const loadPreview = async () => {
    try {
      setLoading(true);

      // Load last 5 messages with replies
      const messagesResponse = await leaguesAPI.getMessages(leagueId, 5, 0, true);
      
      // Load stats
      const statsResponse = await leaguesAPI.getMessageStats(leagueId);

      if (messagesResponse.success) {
        setMessages(messagesResponse.messages || []);
      }

      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }
    } catch (err) {
      console.error('Error loading message preview:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (replyData) => {
    if (!replyingTo) return;
    
    try {
      setSubmittingReply(true);
      
      const response = await leaguesAPI.postMessage(leagueId, {
        ...replyData,
        parent_message_id: replyingTo
      });
      
      if (response.success) {
        // Reload messages to show new reply
        await loadPreview();
        setReplyingTo(null);
      }
    } catch (err) {
      console.error('Error posting reply:', err);
      throw err;
    } finally {
      setSubmittingReply(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Team Logo Component
  const TeamLogo = ({ message }) => {
    if (message.team_logo_url) {
      return (
        <img 
          src={message.team_logo_url}
          alt={`${message.team_name} Logo`}
          className="w-8 h-8 rounded-full object-cover border border-neutral-700"
        />
      );
    }

    const teamColors = message.team_colors || {};
    return (
      <div 
        className="w-8 h-8 rounded-full border border-neutral-700 flex items-center justify-center text-xs font-bold"
        style={{
          background: teamColors.primary ? 
            `linear-gradient(135deg, ${teamColors.primary}, ${teamColors.secondary || teamColors.primary})` : 
            'linear-gradient(135deg, #FFD700, #8B7500)',
          color: teamColors.text || '#000000'
        }}
      >
        {message.team_name?.substring(0, 2).toUpperCase() || 'TM'}
      </div>
    );
  };

  // Single message display component
  const MessageItem = ({ message }) => {
    const isReplyFormOpen = replyingTo === message.message_id;

    return (
      <div className="space-y-2">
        <div 
          className={`p-3 rounded ${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-800/60 ${dynastyTheme.classes.transition}`}
        >
          <div className="flex items-start gap-2">
            <TeamLogo message={message} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className={`font-medium text-sm ${dynastyTheme.classes.text.white} truncate`}>
                    {message.owner_name || 'Unknown'}
                  </span>
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                    {formatTimeAgo(message.created_at)}
                  </span>
                </div>
                {message.reply_count > 0 && (
                  <div className={`flex items-center gap-1 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                    <MessageSquare className="w-3 h-3" />
                    {message.reply_count}
                  </div>
                )}
              </div>
              {renderMessageContent(message.message_text, `text-sm ${dynastyTheme.classes.text.white} mb-2 break-words`)}
              
              {/* Images (if any) */}
              {message.message_data?.images && message.message_data.images.length > 0 && (
                <div className="grid grid-cols-2 gap-1 mb-2">
                  {message.message_data.images.map((img, idx) => (
                    <img 
                      key={idx}
                      src={img.url}
                      alt={`Attachment ${idx + 1}`}
                      className="rounded border border-neutral-700 w-full object-contain max-h-24"
                    />
                  ))}
                </div>
              )}
              
              {/* Reply button */}
              <button
                onClick={() => setReplyingTo(isReplyFormOpen ? null : message.message_id)}
                className={`text-xs flex items-center gap-1 ${dynastyTheme.classes.text.neutralLight} hover:text-yellow-400 ${dynastyTheme.classes.transition}`}
              >
                {isReplyFormOpen ? (
                  <>
                    <X className="w-3 h-3" />
                    Cancel
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-3 h-3" />
                    Reply
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Reply Form */}
        {isReplyFormOpen && (
          <div className="pl-10">
            <MessageComposer
              onSubmit={handleReply}
              onCancel={() => setReplyingTo(null)}
              placeholder="Write a reply..."
              isReply
              autoFocus
              buttonText="Post Reply"
              disabled={submittingReply}
            />
          </div>
        )}

        {/* Replies */}
        {message.replies && message.replies.length > 0 && (
          <div className="pl-10 space-y-2 border-l-2 border-neutral-700">
            {message.replies.map((reply) => (
              <div 
                key={reply.message_id}
                className={`p-2 rounded ${dynastyTheme.classes.bg.darkLighter}`}
              >
                <div className="flex items-start gap-2">
                  <TeamLogo message={reply} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-medium text-xs ${dynastyTheme.classes.text.white} truncate`}>
                        {reply.owner_name || 'Unknown'}
                      </span>
                      <span className={`text-[10px] ${dynastyTheme.classes.text.neutralLight}`}>
                        {formatTimeAgo(reply.created_at)}
                      </span>
                    </div>
                    {renderMessageContent(reply.message_text, `text-xs ${dynastyTheme.classes.text.neutralLight} break-words`)}
                    
                    {/* Reply Images */}
                    {reply.message_data?.images && reply.message_data.images.length > 0 && (
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {reply.message_data.images.map((img, idx) => (
                          <img 
                            key={idx}
                            src={img.url}
                            alt={`Reply attachment ${idx + 1}`}
                            className="rounded border border-neutral-700 w-full object-contain max-h-20"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center">
          <div className={`w-6 h-6 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`} />
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="p-6 text-center">
        <MessageSquare className={`w-12 h-12 mx-auto mb-3 opacity-50 ${dynastyTheme.classes.text.neutralLight}`} />
        <p className={`text-sm mb-3 ${dynastyTheme.classes.text.neutralLight}`}>
          No messages yet
        </p>
        <button 
          onClick={onViewAll}
          className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
        >
          Post the first message
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Stats Header */}
      <div className="flex items-center justify-between p-4 pb-3 border-b border-neutral-700/30">
        <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
          {stats.total_messages} total messages
          {stats.unread_messages > 0 && (
            <span className={dynastyTheme.classes.text.primary}>
              {' '}• {stats.unread_messages} unread
            </span>
          )}
        </div>
        
        {/* New Post button */}
        <button 
          onClick={onViewAll}
          className={`flex items-center gap-1 text-xs px-3 py-1 rounded ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} font-semibold hover:opacity-90 ${dynastyTheme.classes.transition}`}
        >
          <Send className="w-3 h-3" />
          New Post
        </button>
      </div>

      {/* Scrollable Message List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '400px' }}>
        {messages.map((message) => (
          <MessageItem key={message.message_id} message={message} />
        ))}
      </div>

      {/* View All Button */}
      <div className="p-4 pt-3 border-t border-neutral-700/30">
        <button 
          onClick={onViewAll}
          className={`w-full ${dynastyTheme.utils.getComponent('button', 'secondary', 'sm')} flex items-center justify-center gap-2`}
        >
          View Full Message Board
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default MessageBoardPreview;
